// src/pages/preview/WebsitePreview.js
// Thin wrapper around WebsiteRenderer for the Site Editor and standalone preview.
// Adds preview chrome (banner, browser chrome, publish modal) around the pure renderer.
// Public website visitors use PublicWebsite.js + WebsiteRenderer directly.

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '../../components/ToastNotification';
import { ArrowLeft, Eye, Globe } from 'lucide-react';
import { useWebsiteBuilder } from '../../contexts/WebsiteBuilderContext';
import { getPractitionerDisplayName } from '../../utils/getPractitionerDisplayName';
import { getAuth } from 'firebase/auth';
import WebsiteRenderer from '../../components/website-preview/WebsiteRenderer';
import { getPolicies } from '../../services/policies-service';
import { getProfile } from '../../services/firestore-service';

const WebsitePreview = ({ embedded = false, onSectionClick = null, activeSection = null, activePanel = null }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state, actions } = useWebsiteBuilder();
  const toast = useToast();
  const auth = getAuth();

  const [showPublishModal, setShowPublishModal] = useState(false);
  const [policies, setPolicies] = useState(null);
  const [canonicalProfilePhoto, setCanonicalProfilePhoto] = useState(null);

  // Handle Stripe booking cancellation return (?booking=cancelled)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('booking') === 'cancelled') {
      toast.error('Payment cancelled', 'Your booking was not confirmed. No charge was made.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { draft } = state;
  const displayName = getPractitionerDisplayName(state);
  const practitionerId = auth?.currentUser?.uid;

  // Fetch canonical policies so the preview reflects what the public site will render.
  // Falls back to draft.contactFooter inside WebsiteRenderer when canonical is empty.
  useEffect(() => {
    if (!practitionerId) return;
    getPolicies(practitionerId).then(setPolicies).catch(() => {});
  }, [practitionerId]);

  // Fetch canonical profile photo (Settings → Public Profile saves to practitioner.profile.photo,
  // which doesn't sync to draft.profile.photo). Read-with-fallback in WebsiteRenderer uses this.
  useEffect(() => {
    if (!practitionerId) return;
    getProfile(practitionerId)
      .then((p) => setCanonicalProfilePhoto(p?.photo || null))
      .catch(() => {});
  }, [practitionerId]);

  // Read availability from localStorage for BookingPage within the renderer
  const availability = JSON.parse(localStorage.getItem('practitionerAvailability') || '{}');

  const handlePublish = () => {
    actions.publish();
    navigate('/settings/website-content', {
      state: { message: 'Website published successfully! Your changes are now live.' },
    });
  };

  const renderer = (
    <WebsiteRenderer
      design={draft.design}
      hero={draft.hero}
      content={draft.content}
      contactFooter={draft.contactFooter}
      marketing={draft.marketing}
      profile={draft.profile}
      sectionOrder={draft.sectionOrder}
      sectionVisibility={draft.sectionVisibility}
      practitionerId={practitionerId}
      practitionerName={displayName}
      availability={availability}
      embedded={embedded}
      onSectionClick={onSectionClick}
      activeSection={activeSection}
      activePanel={activePanel}
      isPublicSite={false}
      policies={policies}
      canonicalProfilePhoto={canonicalProfilePhoto}
    />
  );

  // Embedded mode — no chrome, just the renderer
  if (embedded) return renderer;

  // Standalone preview mode — preview banner + browser chrome + renderer
  return (
    <div className="min-h-screen bg-gray-50">
      {/* CSS Animation for section labels (used by WebsiteRenderer in embedded mode) */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateX(-50%) translateY(-4px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>

      {/* Preview Mode Banner — Sticky */}
      <div className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div
                role="button"
                tabIndex={0}
                onClick={() => {
                  const returnPath = location.state?.returnPath || '/site-editor';
                  navigate(returnPath);
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    const returnPath = location.state?.returnPath || '/site-editor';
                    navigate(returnPath);
                  }
                }}
                className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors cursor-pointer"
                style={{ WebkitUserSelect: 'none', userSelect: 'none' }}
              >
                <ArrowLeft className="w-4 h-4" style={{ pointerEvents: 'none' }} />
                <span style={{ pointerEvents: 'none' }}>Back to Editing</span>
              </div>

              <div className="h-6 w-px bg-gray-300" />

              <div className="flex items-center gap-2 text-sm">
                <Eye className="w-4 h-4 text-purple-600" />
                <span className="font-medium text-gray-900">Preview Mode</span>
                <span className="text-gray-500">·</span>
                <span className="text-gray-600">Changes not published yet</span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setShowPublishModal(true)}
              className="px-6 py-2.5 text-sm font-semibold bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all shadow-md hover:shadow-lg"
            >
              Publish Now
            </button>
          </div>
        </div>
      </div>

      {/* Website Preview Content */}
      <div className="py-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Desktop Preview */}
          <div className="border border-gray-200 rounded-xl overflow-hidden shadow-lg">
            {/* Browser chrome */}
            <div className="bg-gray-100 px-4 py-2.5 border-b border-gray-200 flex items-center gap-2">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-400" />
                <div className="w-3 h-3 rounded-full bg-yellow-400" />
                <div className="w-3 h-3 rounded-full bg-green-400" />
              </div>
              <div className="flex-1 bg-white rounded px-3 py-1 text-sm text-gray-500 ml-4 flex items-center gap-2">
                <div
                  className="w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center text-white text-[8px] font-bold"
                  style={{ backgroundColor: draft.design?.customColors?.primary || '#7c3aed' }}
                >
                  {displayName ? displayName[0] : 'Y'}
                </div>
                <span>divineframeworks.com/site/yourname</span>
              </div>
            </div>

            {/* Site preview content */}
            {renderer}
          </div>
        </div>
      </div>

      {/* Publish Confirmation Modal */}
      {showPublishModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 bg-purple-50 rounded-lg">
                <Globe className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  Publish Changes to Your Website?
                </h3>
                <p className="text-sm text-gray-600">
                  Your updated content will be visible to visitors on your public website immediately.
                </p>
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setShowPublishModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handlePublish}
                className="px-6 py-2 text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
              >
                Publish Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebsitePreview;
